import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageShell } from "@/components/PageShell";

const PRESETS = [25, 50, 100, 250];

export default function Donate() {
  const [amount, setAmount] = useState(50);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [total, setTotal] = useState({ total: 0, count: 0 });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/donations/total").then((r) => setTotal(r.data)); }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !amount) { toast.error("Please add your name, email and an amount."); return; }
    setBusy(true);
    try {
      await api.post("/donations", { ...form, amount: Number(amount) });
      setDone(true);
      toast.success("Thank you for your generosity!");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <PageShell>
      <div className="max-w-2xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center">
          <Heart className="mx-auto text-green-600" size={36} />
          <h1 className="mt-4 font-display text-4xl lg:text-5xl text-slate-900">Make a Donation</h1>
          <p className="mt-3 text-slate-600 text-lg">Every contribution helps keep Pow's memory alive and supports the day.</p>
        </div>

        <div className="mt-8 rounded-2xl bg-slate-900 text-white p-6 text-center">
          <p className="text-sm text-slate-400 uppercase tracking-wider">Raised so far</p>
          <p className="font-display text-4xl mt-1" data-testid="donation-total">€{Math.round(total.total)}</p>
          <p className="text-sm text-slate-400 mt-1">from {total.count} generous {total.count === 1 ? "donation" : "donations"}</p>
        </div>

        {done ? (
          <div className="mt-8 rounded-2xl border border-green-100 bg-green-50 p-8 text-center" data-testid="donation-done">
            <CheckCircle2 className="mx-auto text-green-600" size={44} />
            <h2 className="mt-4 font-display text-2xl text-slate-900">Thank you, {form.name}!</h2>
            <p className="mt-2 text-slate-600">Your donation of €{amount} has been received. A receipt has been emailed to {form.email}.</p>
            <button onClick={() => { setDone(false); setForm({ name: "", email: "", message: "" }); }} className="mt-5 px-6 py-2.5 rounded-full bg-white font-semibold">Donate again</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="grid grid-cols-4 gap-3">
              {PRESETS.map((p) => (
                <button key={p} type="button" data-testid={`donate-preset-${p}`} onClick={() => setAmount(p)}
                  className={`py-3 rounded-xl font-semibold border-2 transition-all ${Number(amount) === p ? "border-green-600 bg-green-50 text-green-700" : "border-slate-200 text-slate-700 hover:border-green-300"}`}>
                  €{p}
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Custom amount (€)</label>
              <input data-testid="donate-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-green-500 outline-none" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Name</label>
                <input data-testid="donate-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-green-500 outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <input data-testid="donate-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-green-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Message (optional)</label>
              <textarea data-testid="donate-message" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-green-500 outline-none" placeholder="In memory of…" />
            </div>
            <button type="submit" disabled={busy} data-testid="donate-submit" className="w-full py-3.5 rounded-full bg-green-600 text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {busy ? "Processing…" : `Donate €${amount || 0}`}
            </button>
            <p className="text-xs text-slate-400 text-center">Secure payment powered by Stripe. This demo uses a test payment flow.</p>
          </form>
        )}
      </div>
    </PageShell>
  );
}
